
import React from "react";
import { useRef, useState, useEffect } from "react";
import PropTypes from "prop-types";

import DateInput from "./DateInput";
import { MultiViewCalendar } from "@progress/kendo-react-dateinputs";
import { Popup } from "@progress/kendo-react-popup";

import "./DateRangeInput.scss";

/**
 * Date range picker
 * @module
 * @param {string} name - Name
 * @param {string} type - date or datetime or time => choose between to types
 * @param {func} onChange - Function invoked on value change
 * @param {object} [value] - Current input value
 * @param {bool} [disabled] - Is input readonly? => user can not change value
 * @param {bool} [required] - Is input required? => can not be left empty
 * @param {string} [validationMessage] - Validation message if field is invalid
 * @param {Date | number} [min] - Minimum date
 * @param {Date | number} [max] - Maximum date
 * @param {number} [tabIndex] - Tabindex of datepicker => index of input using <tab> to navigate
 * @param {func} [onBlur] - Function invoked on input blur
 *
 *              Date                         Format : format je fixny v tvare "dd.mm.yyyy" !!!!
 */

const START = "START";
const END = "END";

const DateRangeInput = (props) => {
  const [changeState, setChangeState] = useState({
    start: false,
    end: false
  });
  const [calendarOpen, setCalendarOpen] = useState(false);

  const anchor = useRef(null);
  const popupRef = useRef(null);
  const startRef = useRef(null);
  const endRef = useRef(null);
  const calendarRef = useRef(null);

  const _value = useRef(props.value ?? { start: null, end: null });

  useEffect(() => {
    if (calendarOpen) calendarRef?.current.focus();
  }, [calendarOpen]);

  useEffect(() => {
    if (changeState.start && changeState.end) closePopup();
    // eslint-disable-next-line
  }, [changeState]);

  const getValue = () => {
    _value.current = {
      start: startRef.current?.state.date ?? null,
      end: endRef.current?.state.date ?? null
    };
    return _value.current;
  }

  const setStartDate = (date) => {
    if (!date) return this.onChange(null, date, null, this.START);

    startRef.current.onChange({
      value: startRef.current.dateToString(date),
    });
  };

  const setEndDate = (date) => {
    if (!date) return;

    endRef.current.onChange({
      value: endRef.current.dateToString(date),
    });
  };

  const onChange = (from) => {
    setChangeState({
      start: from === START ? true : changeState.start,
      end: from === END ? true : changeState.end,
    });

    if (props.onChange) props.onChange(props.name, getValue());
  }

  const checkOrder = () => {
    const value = _value.current;
    if (!value.start || !value.end) return;
    if (value.start > value.end) {
      let oldStart = value.start;
      let oldEnd = value.end;

      setStartDate(oldEnd);
      setEndDate(oldStart);
    }
  };

  const onFocus = () => {
    if (calendarOpen) return cancelClosePopup();

    if (props.onFocus && !calendarOpen) props.onFocus();

    setCalendarOpen(true);
  };

  const onBlur = () => {
    closePopup();
  };

  const cancelClosePopup = () => {
    clearTimeout(popupRef.current);
  };

  const closePopup = () => {
    clearTimeout(popupRef.current);
    popupRef.current = setTimeout(() => {
      setCalendarOpen(false);
      setChangeState({
        start: false,
        end: false
      });

      checkOrder();

      if (props.onBlur) props.onBlur();
    }, 200);
  };

  const setNewDates = (data) => {
    let newValue = { ...data.value };
    if (!newValue) return;

    if (newValue.start) setStartDate(newValue.start);
    if (newValue.end) setEndDate(newValue.end);
  };


  let value = getValue();
  return (
    <div className="k-dateinput-wrap wrap-range">
      <span
        className="dpop-range"
        ref={anchor}
        onFocus={onFocus}
        onBlur={onBlur}
        tabIndex={props.tabIndex}
      >
        <div className={`form-input range-input ${props.disabled ? "disabled-field" : props.required ? "required-field" : "default-field"}`}>
          <p className={"range-text " + (props.disabled ? "range-disabled" : "")}> Od: </p>
          <DateInput
            type="date"
            // placeholder="Od"
            className={"range-cust-input " + (props.disabled ? "range-cust-input-disabled" : "")}
            // validationMessage=" "
            name={props.name + "-start"}
            value={props.value?.start instanceof Date ? props.value?.start : props.value?.start ? new Date(props.value?.start) : null}
            ref={startRef}
            nobuttons={true}
            onChange={() => onChange(START)}
            onClear={() => onChange(START)}
            min={props.min ?? undefined}
            max={props.max ?? undefined}
            disabled={props.disabled}
            required={props.required}
          />
          <div className="range-filler"></div>
        </div>
        <div className={`form-input range-input ${props.disabled ? "disabled-field" : props.required ? "required-field" : "default-field"}`}>
          <p className={"range-text " + (props.disabled ? "range-disabled" : "")}> Do: </p>
          <DateInput
            type="date"
            //  placeholder="Do"
            className={"range-cust-input " + (props.disabled ? "range-cust-input-disabled" : "")}
            // validationMessage=" "
            name={props.name + "-end"}
            value={props.value?.end instanceof Date ? props.value?.end : props.value?.end ? new Date(props.value?.end) : null}
            ref={endRef}
            nobuttons={true}
            onChange={() => onChange(END)}
            onClear={() => onChange(END)}
            min={props.min ?? undefined}
            max={props.max ?? undefined}
            disabled={props.disabled}
            required={props.required}
          />
          <div className="range-filler"></div>
        </div>
      </span>

      <Popup
        anchor={anchor.current}
        show={calendarOpen}
        ref={popupRef}
      >
        <MultiViewCalendar
          animate={false}
          mode="range"
          activeRangeEnd={value.start ? (value.end ? (changeState.start ? (changeState.end ? null : "end") : "start") : "end") : "start"}
          min={props.min ?? undefined}
          max={props.max ?? undefined}
          value={value}
          onFocus={onFocus}
          onBlur={onBlur}
          onChange={setNewDates}
          ref={calendarRef}
        />
      </Popup>
    </div>
  );
}

DateRangeInput.propTypes = {
  name: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  value: PropTypes.object,
  disabled: PropTypes.bool,
  required: PropTypes.bool,
  validationMessage: PropTypes.string,
  onBlur: PropTypes.func,
  min: PropTypes.instanceOf(Date),
  max: PropTypes.instanceOf(Date),
  tabIndex: PropTypes.number,
};

export default DateRangeInput;